import type { Chassis, Director, Driver, Team } from "../types";
import { baseScore, dnfProbability } from "./race";
import { directorLuckMult } from "./director";
import { makeTeam } from "./team";
import { CALENDAR_SIZE, TRACKS } from "../data/tracks";

/**
 * The concrete, lineup-specific effect of hiring a director, measured against
 * the SAME lineup with no director. Lets the draft show "what this principal
 * does for YOUR two drivers + chassis" instead of abstract stats — so a
 * motivator on a synergy-less lineup honestly previews as +0, while the same
 * motivator on a gelled lineup shows its real gain.
 */
export interface DirectorPreview {
  /** Avg per-car dry score gain across all tracks (captures pace, synergy
   *  amplification, gambler base, development ramp, and home affinity). */
  readonly scoreDelta: number;
  /** Extra per-car score in heavy rain, BEYOND the dry gain (rain master). */
  readonly wetDelta: number;
  /** Change in dry retirement probability, in points (negative = safer). */
  readonly dnfDelta: number;
  /** Multiplier on the race's luck swing: >1 widens it (gambler / aggressive),
   *  <1 narrows it (reliable). 1 = unchanged. */
  readonly luckMult: number;
}

const PREVIEW_TEAM = (d1: Driver, d2: Driver, chassis: Chassis, director: Director | null): Team =>
  makeTeam({ name: "", d1, d2, chassis, director, isPlayer: true });

export function directorPreview(d1: Driver, d2: Driver, chassis: Chassis, director: Director): DirectorPreview {
  const withDir = PREVIEW_TEAM(d1, d2, chassis, director);
  const noDir = PREVIEW_TEAM(d1, d2, chassis, null);
  // Preview the development ramp at its mid-season value (progress 0.5), a fair
  // average of a season that starts at 0 and ends at the peak.
  const mid = (CALENDAR_SIZE - 1) / 2;
  let dry = 0;
  let heavy = 0;
  for (const track of TRACKS) {
    for (const d of [d1, d2]) {
      dry += baseScore(d, withDir, track, "dry", mid, CALENDAR_SIZE) - baseScore(d, noDir, track, "dry", mid, CALENDAR_SIZE);
      heavy +=
        baseScore(d, withDir, track, "heavy_rain", mid, CALENDAR_SIZE) -
        baseScore(d, noDir, track, "heavy_rain", mid, CALENDAR_SIZE);
    }
  }
  const n = TRACKS.length * 2;
  const scoreDelta = dry / n;
  const wetDelta = heavy / n - scoreDelta;
  const dnfDelta =
    (dnfProbability(d1, withDir, "dry") -
      dnfProbability(d1, noDir, "dry") +
      (dnfProbability(d2, withDir, "dry") - dnfProbability(d2, noDir, "dry"))) /
    2;
  return { scoreDelta, wetDelta, dnfDelta, luckMult: directorLuckMult(director) };
}
